/**
 * PocketBase refuses what a plan does not cover with a 403 whose message is "plan:<reason>"
 * (`server/pb_hooks/plans.pb.js`), an invitation it will not send with "invite:<reason>" and a
 * change to a booked document with "document:booked". The PocketBase client turns that into a sentence in the
 * person's language, so every toast that shows an error message explains it without extra code.
 */
import type { TFunction } from "i18next";
import { REFUSAL_PREFIX, type Refusal } from "@/lib/plan/plan";

const REFUSALS: Refusal[] = ["read_only", "receipts", "accounts", "users", "accounting_export"];

/** The reason behind a plan refusal, or null for any other message. */
export function refusalOf(message: unknown): Refusal | null {
  // PocketBase capitalises an error message and ends it with a full stop: "Plan:receipts."
  const normalized = typeof message === "string" ? message.toLowerCase().replace(/\.$/, "") : "";
  if (!normalized.startsWith(REFUSAL_PREFIX)) return null;
  const reason = normalized.slice(REFUSAL_PREFIX.length) as Refusal;
  return REFUSALS.includes(reason) ? reason : null;
}

const INVITE_REFUSALS = [
  "unverified",
  "duplicate",
  "daily_limit",
  "invalid",
  "used",
  "expired",
  "other_email",
] as const;

/** Why an invitation was refused ("invite:<reason>", server/pb_hooks/bookkeeping.pb.js). */
export function inviteRefusalOf(message: unknown): (typeof INVITE_REFUSALS)[number] | null {
  const normalized = typeof message === "string" ? message.toLowerCase().replace(/\.$/, "") : "";
  const reason = normalized.startsWith("invite:") ? normalized.slice("invite:".length) : "";
  return INVITE_REFUSALS.find((known) => known === reason) ?? null;
}

/** A change to a booked document ("document:booked", server/pb_hooks/bookkeeping.pb.js). */
export function isBookedRefusal(message: unknown): boolean {
  const normalized = typeof message === "string" ? message.toLowerCase().replace(/\.$/, "") : "";
  return normalized === "document:booked";
}

/** PocketBase `afterSend`: rewrites a refusal's message, leaves everything else alone. */
export function translateRefusal<T>(data: T, t: TFunction): T {
  const message = (data as { message?: unknown } | null)?.message;
  const reason = refusalOf(message);
  if (reason) (data as { message: string }).message = t(`planLimits.refused.${reason}`);
  const invite = inviteRefusalOf(message);
  if (invite) (data as { message: string }).message = t(`settings.inviteRefused.${invite}`);
  if (isBookedRefusal(message)) (data as { message: string }).message = t("document.lockedRefused");
  return data;
}
